import { useEffect, useRef, useCallback } from 'react';
import useGameStore, { type LoadCurrentSceneOptions } from '../stores/gameStore';

/** Poll every 2s when scene has waiting options so max-time timer expiry is detected quickly. */
const POLLING_INTERVAL_MS = 2000;

/**
 * Keeps scene state aligned with the server:
 * - Refresh on app/tab visibility (always — avoids stale localStorage variable display)
 * - Polling when scene has waiting options (timers / auto-transition)
 */
export function useWaitingSceneRefresh() {
  const gameStore = useGameStore();
  const gameStoreRef = useRef(gameStore);
  gameStoreRef.current = gameStore;

  const hasWaitingOptions =
    Array.isArray(gameStore.waitingOptionIds) && gameStore.waitingOptionIds.length > 0;

  const pollingIntervalRef = useRef<number | null>(null);

  const loadCurrentScene = useCallback(
    (options?: LoadCurrentSceneOptions) => gameStoreRef.current.loadCurrentScene(options),
    []
  );

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const checkAndFireAutoTransition = useCallback(
    () => gameStoreRef.current.checkAndFireAutoTransition?.(),
    []
  );

  // Visibility change: always refresh scene from server when user returns to the app/tab.
  useEffect(() => {
    const handler = async () => {
      if (document.visibilityState !== 'visible') return;
      const { currentSession, currentPlayer } = gameStoreRef.current;
      if (!currentSession || !currentPlayer) return;
      await loadCurrentScene({ backgroundRefresh: true });
      if (hasWaitingOptions) {
        checkAndFireAutoTransition();
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [hasWaitingOptions, loadCurrentScene, checkAndFireAutoTransition]);

  // Polling fallback: refresh when scene has waiting options (e.g. max-time timer); then run auto-transition if applicable
  useEffect(() => {
    if (!hasWaitingOptions) {
      clearPolling();
      return;
    }

    pollingIntervalRef.current = window.setInterval(async () => {
      const { currentSession, currentPlayer } = gameStoreRef.current;
      if (currentSession && currentPlayer) {
        await loadCurrentScene();
        checkAndFireAutoTransition();
      }
    }, POLLING_INTERVAL_MS);

    return clearPolling;
  }, [hasWaitingOptions, loadCurrentScene, checkAndFireAutoTransition, clearPolling]);
}
